import { useQuery } from 'urql'
import { SMS_OPT_OUT_QUERY } from 'app/components/contacts/inbox/graphql'
import { phoneKey } from 'app/lib/smsOptOut'

/**
 * Whether this number has an opt-out record at this location.
 *
 * Asks about the one number rather than reading the location's whole opt-out
 * list (`useLocationOptOuts`, which the conversation list uses for its badges):
 * that list is bounded at a thousand records and under-suppresses past it, which
 * is survivable for a badge and not for a gate that decides whether we may text
 * somebody who replied STOP. `limit: 0` because only the count is read.
 *
 * Existence means outbound is blocked. Keys are the digits-only last 10, which
 * is how the backend normalizes them (OptOut.normalizeKey in trackable-api), and
 * nothing shorter is asked about at all: a partial number would key an opt-out
 * onto the wrong contact.
 */
export function useContactOptedOut(phone: string | null | undefined, locationId: string | undefined): boolean {
  const key = phoneKey(phone)
  const askable = key.length === 10 && !!locationId
  const [result] = useQuery<{ optOuts?: { total?: number } }>({
    query: SMS_OPT_OUT_QUERY,
    variables: {
      search: askable ? `type:phone AND key:${key} AND locationId:${locationId}` : undefined,
      pagination: { limit: 0 },
    },
    pause: !askable,
    requestPolicy: `cache-and-network`,
  })
  return (result.data?.optOuts?.total ?? 0) > 0
}
